import React, { useState } from 'react';
import { fetchUgcReviews } from '../../cabsAPIs';
import './UserRatings.css';
import { UgcReviewsData } from '../../pages/RouteTemplate/RouteDataTypeChecker';
import { ceilToNearestMultiple } from '../../utils/commonUtils';

type ReviewItem = {
  reviewId: string;
  reviewDate: string;
  review: string;
  overallRating: string;
  reviewerName: string;
};

type UserRatingsProps = {
  sourceCityName: string;
  destinationCityName: string;
  sourceCityCode?: string;
  destinationCityCode?: string;
  ugcReviews?: UgcReviewsData;
  overallRating?: number;
  vehicleType?: string;
};

const DEFAULT_TAGS = [
  'Safe Drive',
  'Great Experience',
  'Good Service',
  'Over All Experience Good',
  'Safe Driver'
];

const getRatingLabel = (rating: number): string => {
  if (rating >= 4.5) return 'Excellent';
  if (rating >= 4.0) return 'Very Good';
  if (rating >= 3.5) return 'Good';
  if (rating >= 3.0) return 'Average';
  return 'Below Average';
};

const RatingBar = ({
  label,
  value,
  max = 5
}: {
  label: string;
  value: number;
  max?: number;
}) => {
  const percentage = (value / max) * 100;

  return (
    <div className="userRatings__barRow">
      <span className="userRatings__barLabel">{label}</span>
      <div className="userRatings__barBottom">
        <div className="userRatings__barTrack">
          <div
            className="userRatings__barFill"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="userRatings__barValue">{value.toFixed(1)}</span>
      </div>
    </div>
  );
};

const ThumbsUpIcon = () => (
  <svg
    className="userRatings__thumbsUp"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="#e8a500"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66a4.8 4.8 0 00-.88-1.22L14 2 7.59 8.41C7.21 8.79 7 9.3 7 9.83v7.84A2.33 2.33 0 009.34 20h8.11c.7 0 1.36-.37 1.72-.97l2.66-6.15z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#666"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const StarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const formatDate = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getRatingColor = (rating: number): string => {
  if (rating >= 4.5) return '#0a8f3f';
  if (rating >= 3.5) return '#0c58b4';
  if (rating >= 2.5) return '#e8a500';
  return '#d93025';
};

const UserRatings = ({
  sourceCityName,
  destinationCityName,
  sourceCityCode,
  destinationCityCode,
  ugcReviews,
  overallRating,
  vehicleType
}: UserRatingsProps) => {
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);

  if (!ugcReviews || !ugcReviews.numberOfRating) return null;

  const route = `${sourceCityName} to ${destinationCityName}`;
  const driverRating = ugcReviews.driverRating;
  const cabRating = ugcReviews.vehicleRating;
  const totalReviewers = ugcReviews.numberOfRating;
  const reviewCount = ugcReviews.reviewCount;
  const tags = ugcReviews.uniquePositiveTags;

  const displayRating = overallRating || 0;

  const fetchReviews = async () => {
    if (!sourceCityCode || !destinationCityCode) {
      console.error('Missing sourceCityCode or destinationCityCode');
      return;
    }
    setLoadingReviews(true);
    try {
      const data = await fetchUgcReviews(sourceCityCode, destinationCityCode);
      if (data.bestReviews && Array.isArray(data.bestReviews)) {
        setReviews(data.bestReviews);
      }
    } catch (e) {
      console.error('Failed to fetch reviews', e);
    } finally {
      setLoadingReviews(false);
      // One frame after reviews mount (no --open yet), then add --open
      // so the CSS transition always has a starting state to animate from.
      requestAnimationFrame(() =>
        setTimeout(() => setShowReviewsModal(true), 300)
      );
    }
  };

  const handleSeeAllReviews = () => {
    if (reviews.length > 0) {
      setShowReviewsModal(true);
    } else {
      fetchReviews();
    }
  };

  if (
    Number(displayRating) < 3.5 ||
    Number(driverRating) < 3.5 ||
    Number(cabRating) < 3.5
  ) {
    return null;
  }

  return (
    <section className="userRatings">
      <h2 className="userRatings__title">{`What Travellers Say About ${sourceCityName} to ${destinationCityName} Cabs on MakeMyTrip`}</h2>

      <div className="userRatings__card">
        {/* Inner: rating card + thumbs up grouped together */}
        <div className="userRatings__ratingGroup">
          {/* Inner bordered card: score box + bars */}
          <div className="userRatings__overview">
            <div className="userRatings__scoreBox">
              <span className="userRatings__scoreValue">
                {displayRating ? displayRating.toFixed(1) : '--'}
              </span>
              <span className="userRatings__scoreLabel">
                {displayRating ? getRatingLabel(displayRating) : ''}
              </span>
            </div>
            <div className="userRatings__bars">
              {driverRating != null && (
                <RatingBar label="Driver Rating" value={driverRating} />
              )}
              {cabRating != null && (
                <RatingBar label="Cab Rating" value={cabRating} />
              )}
            </div>
          </div>

          {/* Thumbs up + "Rated by..." — sibling of the inner card */}
          {totalReviewers > 100 && (
            <div className="userRatings__footer">
              <ThumbsUpIcon />
              <span className="userRatings__footerText">
                Rated by{' '}
                <strong>
                  {ceilToNearestMultiple(totalReviewers)} travellers who booked{' '}
                  {vehicleType ? vehicleType : 'cabs'} for {route}
                </strong>
              </span>
            </div>
          )}
        </div>

        {/* Hard separator (mobile only) */}
        <div className="userRatings__separator" />

        {/* Right column on desktop: saying section + button */}
        <div className="userRatings__rightCol">
          {tags && tags.length > 0 && (
            <div className="userRatings__sayingSection">
              <h3 className="userRatings__sayingTitle">
                What people are saying about us?
              </h3>
              <div className="userRatings__tags">
                {tags.map(tag => (
                  <span key={tag} className="userRatings__tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* {!!reviewCount && reviewCount > 0 && (
            <div className="userRatings__btnWrap">
              <button
                className="userRatings__seeAllBtn"
                onClick={handleSeeAllReviews}
                disabled={loadingReviews}
              >
                {loadingReviews ? 'Loading...' : 'See All Reviews'}
              </button>
            </div>
          )} */}
        </div>
      </div>

      {/* Reviews Modal */}
      {reviews.length > 0 && (
        <div
          className={`userRatings__overlay${
            showReviewsModal ? ' userRatings__overlay--open' : ''
          }`}
          onClick={() => setShowReviewsModal(false)}
        >
          <div
            className="userRatings__modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="userRatings__modalHeader">
              <h3 className="userRatings__modalTitle">All Reviews</h3>
              <button
                className="userRatings__modalClose"
                onClick={() => setShowReviewsModal(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="userRatings__reviewsList">
              {reviews.map(review => (
                <div key={review.reviewId} className="userRatings__reviewCard">
                  <div className="userRatings__reviewTop">
                    <div className="userRatings__reviewAvatar">
                      {getInitials(review.reviewerName)}
                    </div>
                    <div className="userRatings__reviewMeta">
                      <span className="userRatings__reviewName">
                        {review.reviewerName}
                      </span>
                      <span className="userRatings__reviewDate">
                        {formatDate(review.reviewDate)}
                      </span>
                    </div>
                    <span
                      className="userRatings__reviewBadge"
                      style={{
                        background: getRatingColor(
                          parseFloat(review.overallRating)
                        )
                      }}
                    >
                      {parseFloat(review.overallRating).toFixed(1)} <StarIcon />
                    </span>
                  </div>
                  <p className="userRatings__reviewText">{review.review}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default UserRatings;
